import {DeepdotsEventType, PopupActions, PopupStyle, FormData} from '../types';
import { buildSurveyIdentity } from '../tracking/tracking-manager';
import { buildFontFaceCss, buildFontFamilyValue } from './font';
import magicfeedback from "@magicfeedback/native";
import magicfeedbackCss from '../assets/style.css';

// Inserta la hoja de estilos de MagicFeedback directamente en el popup para garantizar estilos incluso si el bundler no la inyecta globalmente.
function ensureMagicFeedbackStyles(_popup: HTMLElement) {
    const STYLE_ID = 'magicfeedback-sdk-styles';
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = magicfeedbackCss;
    document.head.appendChild(style);
}

// Añade estilos de spinner si no existen
function ensureSpinnerStyles(_popup: HTMLElement) {
    if (document.getElementById('deepdots-spinner-styles')) return;
    const style = document.createElement('style');
    style.id = 'deepdots-spinner-styles';
    style.textContent = `
    @keyframes ddspin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
    .mf-spinner { display:flex; justify-content:center; align-items:center; padding:8px 0; }
    .mf-spinner-circle { width:28px; height:28px; border:3px solid #e0e6ed; border-top-color:#1E293B; border-radius:50%; animation: ddspin 0.9s linear infinite; }
  `;
    document.head.appendChild(style);
}

function ensureResponsiveStyles(_popup: HTMLElement) {
    if (document.getElementById('deepdots-responsive-styles')) return;
    const style = document.createElement('style');
    style.id = 'deepdots-responsive-styles';
    style.textContent = `
    /* Responsive adjustments */
    @media (max-width: 640px) {
      .deepdots-popup {
        width: calc(100% - 24px) !important;
        max-width: calc(100% - 24px) !important;
        height: auto !important;
        max-height: 90vh !important;
        border-radius: 12px !important;
        padding: calc(16px + env(safe-area-inset-top)) 16px calc(16px + env(safe-area-inset-bottom)) 16px !important;
        box-sizing: border-box;
      }
      .deepdots-popup .mf-spinner-circle { width: 32px; height: 32px; border-width: 4px; }
      .deepdots-popup button { font-size: 16px !important; }
      .deepdots-popup-header button { width:48px; height:48px; }
      .deepdots-popup-header button svg { width:26px; height:26px; }
      .deepdots-popup-footer { flex-direction: column-reverse !important; gap: 12px !important; }
      .deepdots-popup-footer button { width: 100%; }
    }
    @media (max-width: 400px) {
      .deepdots-popup { padding: calc(12px + env(safe-area-inset-top)) 12px calc(12px + env(safe-area-inset-bottom)) 12px !important; }
      .deepdots-popup-header button { width:48px; height:48px; }
      .deepdots-popup-header button svg { width:26px; height:26px; }
    }
    @media (orientation: landscape) and (max-height: 480px) {
      .deepdots-popup {
        height: 100vh !important;
        max-height: 100vh !important;
        overflow-y: auto !important;
      }
    }
  `;
    document.head.appendChild(style);
}

// Inyecta (una sola vez) el @font-face de la fuente personalizada.
function ensureFontFace(family: string, url: string) {
    const css = buildFontFaceCss(family, url);
    if (!css) return;
    const STYLE_ID = 'deepdots-font-face';
    let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!el) {
        el = document.createElement('style');
        el.id = STYLE_ID;
        document.head.appendChild(el);
    }
    if (el.textContent !== css) el.textContent = css;
}

/**
 * Renderiza el popup dentro del contenedor dado usando MagicFeedback para la encuesta.
 */
export async function renderPopup(
    container: HTMLElement,
    surveyId: string,
    productId: string,
    actions: PopupActions | undefined,
    emit: (type: DeepdotsEventType, surveyId: string, data?: Record<string, unknown>) => void,
    onClose: () => void,
    env: string = 'production',
    userId?: string,
    style?: PopupStyle,
    sessionId?: string,
    miniService?: string,
    analyticsFeedbackSessionId?: string,
): Promise<void> {
    let surveyCompletedEmitted = false;
    let stylesInjected = false;

    const isDark = style?.theme === 'dark';
    const theme = {
        popupBg:          isDark ? '#1e1e1e' : '#fff',
        textPrimary:      isDark ? '#f0f0f0' : '#111',
        closeBtnHoverBg:  isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
        closeBtnHoverColor: isDark ? '#fff' : '#000000',
    };

    const positionMap: Record<string, { justifyContent: string; alignItems: string; padding?: string; background: string }> = {
        'center':       { justifyContent: 'center',     alignItems: 'center',                        background: 'rgba(0,0,0,0.5)' },
        'bottom':       { justifyContent: 'center',     alignItems: 'flex-end',   padding: '16px',   background: 'transparent' },
        'bottom-right': { justifyContent: 'flex-end',   alignItems: 'flex-end',   padding: '16px',   background: 'transparent' },
        'bottom-left':  { justifyContent: 'flex-start', alignItems: 'flex-end',   padding: '16px',   background: 'transparent' },
        'top':          { justifyContent: 'center',     alignItems: 'flex-start', padding: '16px',   background: 'transparent' },
        'top-right':    { justifyContent: 'flex-end',   alignItems: 'flex-start', padding: '16px',   background: 'transparent' },
        'top-left':     { justifyContent: 'flex-start', alignItems: 'flex-start', padding: '16px',   background: 'transparent' },
    };
    const pos = positionMap[style?.position ?? 'center'] ?? positionMap['center'];

    // Crear popup base
    const popup = document.createElement('div');
    popup.className = 'deepdots-popup';
    popup.style.cssText = `
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      background: ${theme.popupBg};
      border-radius: 8px;
      padding: 24px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      max-width: 600px;
      width: 90%;
      min-height: 200px;
    `;

    const font = style?.font;
    if (font?.family) {
        const familyValue = buildFontFamilyValue(font.family);
        // El survey vive dentro del contenedor y hereda esta fuente.
        popup.style.fontFamily = familyValue;
        // La variable habilita que el <h2> (Montserrat por defecto) también la use.
        popup.style.setProperty('--deepdots-font', familyValue);
        // Los controles de formulario (button/input/textarea/select) NO heredan
        // font-family por defecto; esta clase activa la regla que los fuerza a heredar.
        popup.classList.add('deepdots-has-font');
        if (font.url) ensureFontFace(font.family, font.url);
    }

    // Sección header (solo botón cerrar)
    const header = document.createElement('div');
    header.className = 'deepdots-popup-header';
    header.style.cssText = 'display:flex; justify-content:flex-end; align-items:center; width:100%;';

    // Botón de cierre (X)
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close popup');
    closeBtn.innerHTML = `
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    closeBtn.style.cssText = `
      background:transparent;
      border:none;
      width:32px;
      height:32px;
      display:flex;
      align-items:center;
      justify-content:center;
      border-radius: 8px;
      cursor:pointer;
      color:${theme.textPrimary};
      padding:4px;
      transition: color .15s ease, transform .15s ease, background .15s ease;
      box-shadow: none !important;
    `;
    closeBtn.onmouseenter = () => {
        closeBtn.style.color = theme.closeBtnHoverColor;
        closeBtn.style.background = theme.closeBtnHoverBg;
        closeBtn.style.transform = 'scale(1.06)';
    };
    closeBtn.onmouseleave = () => {
        closeBtn.style.color = theme.textPrimary;
        closeBtn.style.background = 'transparent';
        closeBtn.style.transform = 'scale(1)';
    };
    closeBtn.onclick = () => {
        emit('popup_clicked', surveyId, {action: 'close_icon'});
        onClose();
    };
    header.appendChild(closeBtn);

    ensureMagicFeedbackStyles(popup);
    ensureSpinnerStyles(popup);
    ensureResponsiveStyles(popup);

    const containerContent = document.createElement('div');
    containerContent.className = 'deepdots-popup-container-conetent';
    containerContent.style.cssText = `
    display:flex; 
    flex-direction:column; 
    padding: 0 20px 12px 20px;
      max-height: 80vh; /* límite general del popup */
      overflow: hidden; /* quita scroll del contenedor principal */
`

    // Sección principal (main) - Contenedor formulario + spinner
    const main = document.createElement('div');
    main.className = 'deepdots-popup-main';
    main.style.cssText = 'display:flex; flex-direction:column; width:100%; max-height:80vh; overflow-y:auto;';

    const formWrapper = document.createElement('div');
    formWrapper.style.cssText = 'width:100%; flex: 1 1 auto;';

    // Contenedor de aviso de error de validación
    const errorHint = document.createElement('div');
    errorHint.className = 'deepdots-error-hint';
    errorHint.style.cssText = `
      display: none;
      margin: 12px 0 0 0;
      padding: 10px 12px;
      border-radius: 6px;
      background: #FEF3C7; /* amber-100 */
      color: #92400E; /* amber-700 */
      border: 1px solid #FCD34D; /* amber-300 */
      font-size: 13px;
    `;
    errorHint.setAttribute('role', 'alert');
    errorHint.setAttribute('aria-live', 'polite');

    const spinnerEl = document.createElement('div');
    spinnerEl.className = 'mf-spinner';
    spinnerEl.setAttribute('role', 'status');
    spinnerEl.setAttribute('aria-label', 'Loading survey');
    spinnerEl.innerHTML = '<div class="mf-spinner-circle"></div>';
    spinnerEl.style.cssText = 'position:absolute; top:50%; left:50%; transform:translate(-50%,-50%);';

    const formDivId = `magicfeedback-form-${surveyId}`;
    const formHost = document.createElement('div');
    formHost.id = formDivId;
    formHost.style.cssText = 'width:100%; visibility:hidden;';

    formWrapper.appendChild(spinnerEl);
    formWrapper.appendChild(formHost);
    main.appendChild(formWrapper);
    // Insertar el hint justo antes del footer
    main.appendChild(errorHint);

    // Sección footer (acciones) - botones en extremos
    const footer = document.createElement('div');
    footer.className = 'deepdots-popup-footer';
    footer.setAttribute('data-actions-wrapper', 'true');
    footer.style.cssText = 'display:flex; flex-direction: row-reverse ;justify-content:space-between; align-items:center; gap:8px; margin-top:auto; width:100%; padding-top:16px;';

    // Botones
    const backButton = document.createElement('button');
    backButton.textContent = actions?.back ? actions.back.label : 'Back';
    backButton.style.cssText = `
      background: transparent;
      color: #333;
      border: 1px solid #999;
      padding: 12px 24px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      transition: filter .15s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    `;
    backButton.onmouseenter = () => {
        backButton.style.filter = 'brightness(0.9)';
    }
    backButton.onmouseleave = () => {
        backButton.style.filter = 'brightness(1)';
    }
    backButton.onclick = () => {
        emit('popup_clicked', surveyId, {action: 'back'});
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (formInstance as any)?.back?.();
    };

    // Boton start survyes, solo aprece cuando la encuesta empieza con mensaje de inicio
    // Width de 100% para que ocupe todo el espacio disponible
    const startButton = document.createElement('button');
    startButton.textContent = actions?.start ? actions.start.label : 'Start survey';
    startButton.style.cssText = `
      background: #1E293B;
      color: #fff;
      border: none;
      padding: 12px 24px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      transition: filter .15s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    `;
    startButton.onclick = () => {
        emit('popup_clicked', surveyId, {action: 'start_survey'});
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (formInstance as any)?.startForm?.();
    };

    // Botón cerrar popup, solo aparece al terminar la encuesta
    // Width de 100% para que ocupe todo el espacio disponible
    const closeButton = document.createElement('button');
    closeButton.textContent = actions?.complete ? actions.complete.label : 'Complete survey';
    closeButton.style.cssText = `
      background: #1E293B;
      color: #fff;
      border: none;
      padding: 12px 24px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      transition: filter .15s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    `;
    closeButton.onmouseenter = () => {
        closeButton.style.filter = 'brightness(0.9)';
    }
    closeButton.onmouseleave = () => {
        closeButton.style.filter = 'brightness(1)';
    }
    closeButton.onclick = () => {
        emit('popup_clicked', surveyId, {action: 'complete'});
        onClose();
    };

    // Botón send, si es primera pagina ocupara el espacio completo pero si no estara al lado derecho
    const submitButton = document.createElement('button');
    submitButton.textContent = actions?.accept ? actions.accept.label : 'Send';
    submitButton.style.cssText = `
      background: #1E293B;
      color: #fff;
      border: none;
      padding: 12px 24px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      transition: filter .15s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    `;
    submitButton.onclick = () => {
        if (!surveyCompletedEmitted) {
            emit('popup_clicked', surveyId, {action: 'manual_send'});
            // Dispara envío nativo si existe
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (formInstance as any)?.send?.();
        }
    };

    backButton.style.display = 'none';
    startButton.style.display = 'none';
    submitButton.style.display = 'none';
    closeButton.style.display = 'none';

    // Insertar botones dentro del footer en orden visual (row-reverse deja primario a la derecha)
    footer.appendChild(submitButton);
    footer.appendChild(backButton);
    footer.appendChild(closeButton);
    footer.appendChild(startButton);

    // Añadir footer al main y main al containerContent
    main.appendChild(footer);
    containerContent.appendChild(main);

    // Helper para controlar visibilidad de botones según estado
    type ViewState = 'loading' | 'start' | 'in_progress_first' | 'in_progress_next' | 'completed' | 'error';

    function updateButtons(state: ViewState) {
        // Por defecto, ocultar todos
        backButton.style.display = 'none';
        startButton.style.display = 'none';
        submitButton.style.display = 'none';
        closeButton.style.display = 'none';
        // Reset widths por estado
        backButton.style.width = '';
        startButton.style.width = '';
        submitButton.style.width = '';
        closeButton.style.width = '';

        switch (state) {
            case 'loading':
                // Footer se ocultará desde setLoading
                break;
            case 'start':
                // Solo botón Start a ancho completo
                startButton.style.display = 'inline-flex';
                startButton.style.width = '100%';
                break;
            case 'in_progress_first':
                // Solo botón Send (lado derecho), ancho auto
                submitButton.style.display = 'inline-flex';
                submitButton.style.width = '';
                setLoading(false);
                break;
            case 'in_progress_next':
                // Mostrar Back (izquierda) + Send (derecha)
                backButton.style.display = 'inline-flex';
                submitButton.style.display = 'inline-flex';
                setLoading(false);
                break;
            case 'completed':
                // Mostrar Close/Complete a ancho completo como acción principal
                closeButton.style.display = 'inline-flex';
                closeButton.style.width = '100%';
                setLoading(false);
                break;
            case 'error':
                // En error, permitir cerrar (ancho auto)
                // closeButton.style.display = 'inline-flex';
                setLoading(false);
                break;
        }
    }

    // Ensamblar popup
    popup.appendChild(header);
    popup.appendChild(containerContent);

    container.innerHTML = '';
    container.appendChild(popup);
    container.style.display = 'flex';
    container.style.justifyContent = pos.justifyContent;
    container.style.alignItems = pos.alignItems;
    container.style.background = pos.background;
    if (pos.padding) container.style.padding = pos.padding;

    // Gestión dinámica de loading
    function setLoading(isLoading: boolean) {
        spinnerEl.style.display = isLoading ? 'flex' : 'none';
        if (!isLoading) {
            formHost.style.visibility = 'visible';
        }
        // Ocultar totalmente los botones cuando está cargando
        footer.style.display = isLoading ? 'none' : 'flex';
        // Ajustar estados de los botones por si se muestran
        backButton.disabled = isLoading;
        startButton.disabled = isLoading;
        closeButton.disabled = isLoading;
        submitButton.disabled = isLoading;
        submitButton.style.opacity = isLoading ? '0.6' : '1';
        submitButton.style.cursor = isLoading ? 'not-allowed' : 'pointer';
        // No sobrescribir el estado de botones al finalizar la carga.
        if (isLoading) {
            updateButtons('loading');
        }
    }

    // Estado inicial
    setLoading(true);

    // Entorno navegador
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    // Referencia instancia formulario
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let formInstance: any = null;

    try {
        if (!magicfeedback || typeof magicfeedback.form !== 'function') {
            console.warn('[MagicFeedback] form() no disponible. Fallback manual.');
            setLoading(false);
            return;
        }
        magicfeedback.init({
            debug: true,
            env: env === 'production' ? 'prod' : 'dev'}
        );
        // Contrato Fase 1 §5: profile = identidad del usuario; metadata = contexto de tracking (session_id + user_id)
        const { profile, metadata } = buildSurveyIdentity(userId ?? null, sessionId ?? null, miniService ?? null, analyticsFeedbackSessionId ?? null);
        formInstance = magicfeedback.form(surveyId, productId, profile, metadata);

        interface TypedGenerateOptions {
            addButton: boolean;
            getMetaData: boolean;
            onLoadedEvent?: (args: {
                formData: FormData,
                progress?: number, total?: number
            }) => void;
            beforeSubmitEvent?: () => void;
            afterSubmitEvent?: (args: { error?: string, completed: boolean, progress: number, total: number }) => void;
            onBackEvent?: (args: { error?: string, progress: number, total: number, followup: boolean }) => void;
        }

        const generateOptions: TypedGenerateOptions = {
            addButton: false,
            getMetaData: true,
        };
        generateOptions.onLoadedEvent = ({formData}) => {
            // Calcular altura disponible y aplicarla al main (restando header + footer + paddings)
            try {
                /*
                const headerHeight = header.getBoundingClientRect().height;
                const footerHeight = footer.getBoundingClientRect().height;
                const paddingY = 48; // 24px top + 24px bottom
                const viewportLimit = window.innerHeight * 0.8; // coincide con max-height del popup
                const available = viewportLimit - headerHeight - footerHeight - paddingY;
                if (available > 120) { // asegurar un mínimo razonable
                    main.style.maxHeight = available + 'px';
                } */
            } catch (e) {
                // silencioso
            }
            // Personalización del popup basada en formData.style
            const s = formData?.style;
            if (s && !stylesInjected) {
                stylesInjected = true;
                // Fondo del contenedor popup
                if (s.boxBackgroundColor) {
                    popup.style.background = s.boxBackgroundColor;
                }
                // Alineación del contenido principal
                if (s.contentAlign) {
                    // 'top' => start, 'center' => center
                    main.style.justifyContent = s.contentAlign === 'center' ? 'center' : 'flex-start';
                }
                // Botón primario (submit, start)
                if (s.buttonPrimaryColor) {
                    submitButton.style.background = s.buttonPrimaryColor;
                    submitButton.style.border = 'none';
                    submitButton.style.color = '#fff';

                    startButton.style.background = s.buttonPrimaryColor;
                    startButton.style.border = 'none';
                    startButton.style.color = '#fff';

                    closeButton.style.background = s.buttonPrimaryColor;
                    closeButton.style.border = 'none';
                    closeButton.style.color = '#fff';
                }
                // Botón secundario (back) — outlined: fondo blanco, letra y borde del color secundario
                if (s.buttonSecondaryColor) {
                    backButton.style.background = '#fff';
                    backButton.style.color = s.buttonSecondaryColor;
                    backButton.style.border = `1px solid ${s.buttonSecondaryColor}`;
                }
                if (s.logo) {
                    if (!document.getElementById('deepdots-popup-logo')) {
                        // Insertar logo si existe
                        const logoImg = document.createElement('img');
                        logoImg.id = 'deepdots-popup-logo';
                        logoImg.src = s.logo;
                        logoImg.alt = 'Logo';
                        logoImg.style.cssText = 'max-height:40px; max-width:100%; object-fit:contain;';
                        if (s.logoSize) {
                            switch (s.logoSize) {
                                case 'small':
                                    logoImg.style.maxHeight = '30px';
                                    break;
                                case 'medium':
                                    logoImg.style.maxHeight = '50px';
                                    break;
                                case 'large':
                                    logoImg.style.maxHeight = '70px';
                                    break;
                            }
                        }
                        if (s.logoPosition) {
                            switch (s.logoPosition) {
                                case 'left':
                                    logoImg.style.margin = '0 16px 42px 0';
                                    logoImg.style.display = 'block';
                                    logoImg.style.marginLeft = '0';
                                    break;
                                case 'right':
                                    logoImg.style.margin = '0 0 42px 16px';
                                    logoImg.style.display = 'block';
                                    logoImg.style.marginLeft = 'auto';
                                    break;
                                case 'center':
                                    logoImg.style.margin = '0 auto 42px auto';
                                    logoImg.style.display = 'block';
                                    break;
                            }
                        }
                        // Insertar antes del main si no existe ya
                        containerContent.insertBefore(logoImg, main);
                    }
                }

                if (s.startMessage && s.startMessage !== '') {
                    // Si hay mensaje de inicio, mostrar botón Start inicialmente
                    console.log(s.startMessage);
                    updateButtons('start');
                } else {
                    // Si no hay mensaje de inicio, mostrar estado de primera página (solo Send)
                    updateButtons('in_progress_first');
                }
            } else {
                // Sin estilos, asumir primera página normal
                updateButtons('in_progress_first');
            }

            emit('popup_clicked', surveyId, {action: 'loaded'});
            setLoading(false); // hace visible el formulario y oculta el spinner
        };
        generateOptions.beforeSubmitEvent = () => {
            setLoading(true);
            emit('popup_clicked', surveyId, {action: 'before_submit'});
        };
        generateOptions.afterSubmitEvent = ({error, completed, total, progress}) => {
            // No cambiar estado de loading aquí; lo gestiona cada transición
            // Normalizar el error a texto seguro
            const errText = error ? (typeof error === 'string' ? error : ((error as unknown as {message?: string}).message ?? String(error))) : '';
            if (errText) {
                // Desactivar loading para que se vean los botones
                setLoading(false);
                // Caso específico: error de pregunta obligatoria
                if (errText.toLowerCase().includes('no response')) {
                    errorHint.textContent = 'Please answer the required question to continue.';
                    errorHint.style.display = 'block';
                    emit('popup_clicked', surveyId, {action: 'validation_error_required'});
                    updateButtons('in_progress_next');
                    return;
                }
                // Otros errores: mostrar mensaje genérico y permitir cerrar
                errorHint.textContent = 'An error occurred while submitting. Please try again or close the popup.';
                errorHint.style.display = 'block';
                emit('popup_clicked', surveyId, {action: 'submit_error', error: errText});
                // updateButtons('error');
                return;
            }
            // Limpiar hint si no hay error
            errorHint.style.display = 'none';
            setLoading(false);
            if (completed) {
                emit('survey_completed', surveyId, {action: 'completed'});
                surveyCompletedEmitted = true;
                updateButtons('completed');
                return;
            }
            emit('popup_clicked', surveyId, {action: 'partial'});
            if (total > 1 && progress > 0 && progress < total) {
                updateButtons('in_progress_next');
            } else {
                updateButtons('in_progress_first');
            }
        };
        generateOptions.onBackEvent = ({progress}) => {
            emit('popup_clicked', surveyId, {action: 'back'});
            // Si volvemos a la primera página, ocultar Back
            if (progress === 0) {
                updateButtons('in_progress_first');
            } else {
                updateButtons('in_progress_next');
            }
        };

        // Ejecutar generación con opciones tipadas
        formInstance.generate(formDivId, generateOptions)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .catch((err: any) => {
                console.error('[MagicFeedback] Error generating form:', err);
                setLoading(false);
            });
    } catch (e) {
        console.error('[MagicFeedback] Exception initializing form:', e);
        setLoading(false);
    }
}
